<template>
	<div class="borrow-panel" :class="{ 'is-compact': compact }">
		<div class="borrow-panel__title"><span class="borrow-panel__title-dot"></span>周转小金库</div>
		<div class="borrow-panel__stats">
			<div class="borrow-panel__stat">
				<span class="borrow-panel__stat-label">当前负债</span>
				<span class="borrow-panel__stat-value is-warn">{{ fmtMoney(debt) }}</span>
			</div>
			<div class="borrow-panel__stat">
				<span class="borrow-panel__stat-label">已付利息</span>
				<span class="borrow-panel__stat-value">{{ fmtMoney(interestPaid) }}</span>
			</div>
			<div class="borrow-panel__stat">
				<span class="borrow-panel__stat-label">利率</span>
				<span class="borrow-panel__stat-value">{{ fmtPercent(rate) }}/天</span>
			</div>
		</div>
		<div class="borrow-panel__tiers">
			<span
				v-for="tier in tiers"
				:key="tier.limit"
				class="borrow-panel__tier"
				:class="{ 'is-active': tier.limit === activeTierLimit }"
			>
				≤{{ fmtMoney(tier.limit * 100) }} {{ fmtPercent(tier.rate) }}
			</span>
		</div>
		<div class="borrow-panel__ops">
			<label class="borrow-panel__input-wrap">
				<span class="borrow-panel__input-prefix">$</span>
				<input
					v-model.number="amountDollars"
					type="number"
					class="borrow-panel__input"
					min="0"
					step="10"
					:disabled="disabled"
				/>
			</label>
			<button class="borrow-panel__btn borrow-panel__btn--borrow" :disabled="disabled" @click="doBorrow">借入</button>
			<button class="borrow-panel__btn borrow-panel__btn--repay" :disabled="disabled" @click="doRepay">还款</button>
		</div>
		<div class="borrow-panel__hint">每 {{ daySeconds }} 秒计 1 天利息，最多可借 {{ fmtMoney(maxCents) }}</div>
	</div>
</template>

<script>
import { fmtMoney, fmtPercent } from '@/util/fx/format'

export default {
	name: 'BorrowPanel',
	props: {
		debt: {
			type: Number,
			default: 0
		},
		interestPaid: {
			type: Number,
			default: 0
		},
		rate: {
			type: Number,
			default: 0
		},
		tiers: {
			type: Array,
			default: () => []
		},
		activeTierLimit: {
			type: Number,
			default: null
		},
		maxCents: {
			type: Number,
			default: 0
		},
		daySeconds: {
			type: Number,
			default: 30
		},
		disabled: {
			type: Boolean,
			default: false
		},
		compact: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			amountDollars: '1000'
		}
	},
	methods: {
		fmtMoney,
		fmtPercent,
		/**
		 * 上抛借入事件（分）
		 */
		doBorrow() {
			const cents = Math.round((Number(this.amountDollars) || 0) * 100)
			this.$emit('borrow', cents)
		},
		/**
		 * 上抛还款事件（分）
		 */
		doRepay() {
			const cents = Math.round((Number(this.amountDollars) || 0) * 100)
			this.$emit('repay', cents)
		}
	}
}
</script>

<style scoped>
.borrow-panel {
	background: linear-gradient(180deg, var(--fx-panel-2), var(--fx-panel));
	border: 1px solid var(--fx-border);
	border-top: 2px solid rgba(245, 177, 61, 0.5);
	border-radius: 10px;
	padding: 10px 12px;
	display: flex;
	flex-direction: column;
	gap: 7px;
}

.borrow-panel__title {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 600;
	color: var(--fx-text);
	letter-spacing: 0.04em;
}

.borrow-panel__title-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: var(--fx-warn);
	box-shadow: 0 0 8px rgba(245, 177, 61, 0.6);
	flex-shrink: 0;
}

.borrow-panel__stats {
	display: flex;
	justify-content: space-between;
	gap: 6px;
}

.borrow-panel__stat {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
}

.borrow-panel__stat-label {
	font-size: 12px;
	color: var(--fx-text-faint);
	letter-spacing: 0.04em;
}

.borrow-panel__stat-value {
	font-size: 14px;
	font-weight: 700;
	font-family: var(--fx-mono);
	font-variant-numeric: tabular-nums;
	color: var(--fx-text);
	white-space: nowrap;
}

.borrow-panel__stat-value.is-warn {
	color: var(--fx-warn);
}

.borrow-panel__tiers {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.borrow-panel__tier {
	font-size: 11.5px;
	font-family: var(--fx-mono);
	padding: 1px 6px;
	border-radius: 999px;
	border: 1px solid var(--fx-border);
	color: var(--fx-text-faint);
	white-space: nowrap;
}

.borrow-panel__tier.is-active {
	border-color: rgba(245, 177, 61, 0.6);
	background: var(--fx-warn-soft);
	color: #f7c269;
}

.borrow-panel__ops {
	display: flex;
	gap: 6px;
}

.borrow-panel__input-wrap {
	position: relative;
	flex: 1;
	min-width: 0;
}

.borrow-panel__input-prefix {
	position: absolute;
	left: 9px;
	top: 50%;
	transform: translateY(-50%);
	font-size: 13px;
	color: var(--fx-text-faint);
	font-family: var(--fx-mono);
	pointer-events: none;
}

.borrow-panel__input {
	width: 100%;
	box-sizing: border-box;
	padding: 6px 8px 6px 20px;
	border-radius: 8px;
	border: 1px solid var(--fx-border);
	background: rgba(255, 255, 255, 0.04);
	color: var(--fx-text);
	font-size: 14px;
	font-family: var(--fx-mono);
	font-variant-numeric: tabular-nums;
	outline: none;
	transition: border-color 0.15s, box-shadow 0.15s;
}

.borrow-panel__input:focus {
	border-color: rgba(245, 177, 61, 0.55);
	box-shadow: 0 0 0 3px rgba(245, 177, 61, 0.12);
}

.borrow-panel__input::-webkit-outer-spin-button,
.borrow-panel__input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.borrow-panel__input[type='number'] {
	-moz-appearance: textfield;
	appearance: textfield;
}

.borrow-panel__btn {
	padding: 6px 13px;
	border: none;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.02em;
	cursor: pointer;
	transition: transform 0.15s, box-shadow 0.2s, filter 0.15s;
}

.borrow-panel__btn:disabled {
	opacity: 0.38;
	cursor: not-allowed;
}

.borrow-panel__btn--borrow {
	background: linear-gradient(180deg, #f7bd52, #e09c2b);
	color: #2b1d04;
	box-shadow: 0 3px 12px rgba(245, 177, 61, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.borrow-panel__btn--borrow:hover:not(:disabled) {
	transform: translateY(-1px);
	filter: brightness(1.05);
	box-shadow: 0 6px 18px rgba(245, 177, 61, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.borrow-panel__btn--repay {
	background: transparent;
	color: var(--fx-text-dim);
	border: 1px solid var(--fx-border);
}

.borrow-panel__btn--repay:hover:not(:disabled) {
	border-color: var(--fx-border-strong);
	color: var(--fx-text);
	background: rgba(255, 255, 255, 0.05);
}

.borrow-panel__hint {
	font-size: 12px;
	line-height: 1.4;
	color: var(--fx-text-faint);
}

/* 面板被拖窄时的紧凑布局：进一步缩小字号，避免标签与按钮挤压 */
.borrow-panel.is-compact {
	padding: 7px 9px;
	gap: 6px;
}

.borrow-panel.is-compact .borrow-panel__title {
	font-size: 12px;
}

.borrow-panel.is-compact .borrow-panel__stat-label {
	font-size: 10.5px;
}

.borrow-panel.is-compact .borrow-panel__stat-value {
	font-size: 13px;
}

.borrow-panel.is-compact .borrow-panel__tiers {
	gap: 3px;
}

.borrow-panel.is-compact .borrow-panel__tier {
	font-size: 10px;
	padding: 1px 4px;
}

.borrow-panel.is-compact .borrow-panel__ops {
	gap: 5px;
}

.borrow-panel.is-compact .borrow-panel__input {
	padding: 5px 6px 5px 18px;
	font-size: 13px;
}

.borrow-panel.is-compact .borrow-panel__btn {
	padding: 5px 10px;
	font-size: 12px;
}

.borrow-panel.is-compact .borrow-panel__hint {
	font-size: 10.5px;
}
</style>
